import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ppdDashboardAPI, activityAPI } from '../../services/api';
import StatCard from './shared/StatCard';
import QuickActionButton from './shared/QuickActionButton';
import CompletionRateChart from './shared/CompletionRateChart';
import Swal from 'sweetalert2';
import './PPDDashboard.css';

const PPDDashboard = ({ ppdCode: propPpdCode, activityId: propActivityId, onBack }) => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selectedActivityId, setSelectedActivityId] = useState(propActivityId || null);

  // Determine if this is embedded mode (props provided) or standalone mode (JWT-based)
  const isEmbedded = !!propPpdCode;

  // Load activities on mount
  useEffect(() => {
    loadActivities();
  }, []);

  // Reload dashboard when activity selection changes
  useEffect(() => {
    if (activities.length > 0) {
      loadDashboardData();
    }
  }, [selectedActivityId, activities.length]);

  const loadActivities = async () => {
    try {
      const response = await activityAPI.getActivities();
      if (response.success) {
        // API returns paginated result: { data: { data: [...], page, totalCount } }
        const activitiesArray = response.data.data || [];
        setActivities(activitiesArray);
        // Set default to active activity (if available)
        const activeActivity = activitiesArray.find(a => a.status?.toUpperCase() === 'ACTIVE');
        if (activeActivity) {
          setSelectedActivityId(activeActivity.activityId);
        }
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use prop activityId or selected activityId
      const activityIdToUse = propActivityId || selectedActivityId;

      // Pass ppdCode if in embedded mode (Admin drill-down)
      const response = await ppdDashboardAPI.getPPDDashboard(activityIdToUse, propPpdCode);

      if (response.success) {
        setDashboardData(response.data);
      } else {
        setError(response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Failed to load PPD dashboard data:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Complete':
        return 'completed';
      case 'On Track':
        return 'on-track';
      case 'Urgent':
        return 'urgent';
      default:
        return 'pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-MY');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-MY');
  };

  const handleReopenSchool = async (school) => {
    // Only allow reopening if school status is COMPLETED
    if (school.schoolStatus?.toUpperCase() !== 'COMPLETED') {
      await Swal.fire({
        icon: 'info',
        title: 'Cannot Reopen',
        text: 'This school is not in COMPLETED status',
        confirmButtonColor: '#2196F3'
      });
      return;
    }

    // Show confirmation dialog with school details
    const result = await Swal.fire({
      title: 'Reopen School Submission?',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <p><strong>School Code:</strong> ${school.schoolCode}</p>
          <p><strong>School Name:</strong> ${school.schoolName}</p>
          <p><strong>Current Status:</strong> <span style="color: #4caf50; font-weight: bold;">${school.schoolStatus}</span></p>
          <p><strong>Completion Rate:</strong> ${school.completionRate}% (${school.closedTahun}/${school.totalTahun} grades closed)</p>
          ${school.lastUpdatedBy ? `<p><strong>Last Updated By:</strong> ${school.lastUpdatedBy}</p>` : ''}
          ${school.lastUpdatedAt ? `<p><strong>Last Updated:</strong> ${formatDateTime(school.lastUpdatedAt)}</p>` : ''}
          <hr style="margin: 15px 0;" />
          <p style="color: #ff9800; font-weight: 500;">
            <i class="fas fa-exclamation-triangle"></i>
            This will change the school status to OPEN and allow the headmaster to re-submit data.
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff9800',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Reopen School',
      cancelButtonText: 'Cancel',
      width: '600px'
    });

    if (!result.isConfirmed) {
      return;
    }

    // Show loading
    Swal.fire({
      title: 'Reopening School...',
      html: 'Please wait while we update the school status',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // Call API to reopen school
      const activityIdToUse = propActivityId || selectedActivityId;
      const response = await ppdDashboardAPI.reopenSchool(school.schoolCode, activityIdToUse);

      if (response.success) {
        await Swal.fire({
          icon: 'success',
          title: 'School Reopened!',
          text: `${school.schoolName} has been reopened successfully. The headmaster can now re-submit data.`,
          confirmButtonColor: '#4caf50'
        });

        // Refresh dashboard
        await loadDashboardData();
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Failed to Reopen School',
          text: response.message || 'An error occurred while reopening the school',
          confirmButtonColor: '#d33'
        });
      }
    } catch (error) {
      console.error('Error reopening school:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to reopen school. Please try again.',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{error}</p>
        <button onClick={loadDashboardData} className="btn btn-primary">
          <i className="fas fa-redo"></i> Retry
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-error">
        <i className="fas fa-info-circle"></i>
        <p>No dashboard data available</p>
      </div>
    );
  }

  const { overview, schools, activityProgress, issueReports, recentActivities } = dashboardData;

  // Use prop PPD code if provided (embedded mode), otherwise use data from API
  const displayPpdCode = propPpdCode || dashboardData.ppdCode;

  return (
    <div className="ppd-dashboard">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2>District Dashboard - {displayPpdCode}</h2>
        <div className="header-buttons">
          {onBack && (
            <button onClick={onBack} className="header-btn btn-back" title="Back to All Districts">
              <i className="fas fa-arrow-left"></i>
            </button>
          )}
          <button onClick={loadDashboardData} className="header-btn btn-refresh" title="Refresh Dashboard">
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      {/* PPD Info and Activity Selector */}
      <div className="top-section">
        <div className="left-info">
          <p><strong>DAERAH:</strong> {displayPpdCode}</p>
          <p><strong>PENGGUNA:</strong> {user?.name || user?.email}</p>
        </div>
        <div className="right-info">
          {!propActivityId && (
            <p>
              <strong>ACTIVITY:</strong>{' '}
              <select
                id="activity-select"
                value={selectedActivityId || ''}
                onChange={(e) => setSelectedActivityId(e.target.value ? parseInt(e.target.value) : null)}
                className="activity-dropdown"
              >
                {activities.map((activity) => (
                  <option key={activity.activityId} value={activity.activityId}>
                    {activity.activityName || `Activity ${activity.enrollYear}`}
                    {activity.status?.toUpperCase() === 'ACTIVE' ? ' (Active)' : ''}
                  </option>
                ))}
              </select>
            </p>
          )}
          {propActivityId && activityProgress && (
            <p><strong>ACTIVITY:</strong> {activityProgress.activityName}</p>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          icon="fa-school"
          value={overview.totalSchools}
          label="Total Schools"
          color="primary"
        />
        <StatCard
          icon="fa-users"
          value={overview.totalStudents.toLocaleString()}
          label="Total Students"
          color="purple"
        />
        <StatCard
          icon="fa-exclamation-circle"
          value={overview.openIssues}
          label="Open Issues"
          color={overview.openIssues > 0 ? "warning" : "success"}
        />
        <StatCard
          icon="fa-check-circle"
          value={`${overview.completedSchools}/${overview.totalSchools}`}
          label="Schools Completed"
          color="success"
        />
      </div>

      {/* Quick Actions */}
      <div className="dashboard-section">
        <h3><i className="fas fa-bolt"></i> Quick Actions</h3>
        <div className="quick-actions-grid">
          <QuickActionButton
            icon="fa-chart-bar"
            label="Report Summary"
            path="/report-summary"
            color="primary"
          />
          <QuickActionButton
            icon="fa-exclamation-circle"
            label="Issue Management"
            path="/issue-reports"
            color="warning"
          />
          <QuickActionButton
            icon="fa-school"
            label="School Reports"
            path="/report-school"
            color="info"
          />
          <QuickActionButton
            icon="fa-users"
            label="Student Listing"
            path="/report-student-listing"
            color="purple"
          />
        </div>
        <div className="download-buttons-row">
          <a className="download-icon-btn download-pdf" href="/docs/Sukatan Pakaian dan Kasut 2026 v2.pdf" download title="Sukatan Pakaian & Kasut 2026">
            <i className="fas fa-file-pdf"></i>
          </a>
          <a className="download-icon-btn download-excel" href="/docs/KTC-Student-Input-Form-Template 2026.xlsx" download title="Borang Input Pelajar 2026">
            <i className="fas fa-file-excel"></i>
          </a>
          <a className="download-icon-btn download-word" href="/docs/KTC System(PPD).docx" download title="Manual Pengguna (PPD)">
            <i className="fas fa-file-word"></i>
          </a>
          <a className="download-icon-btn download-word" href="/docs/KTC SYSTEM(Headmaster).docx" download title="Manual Pengguna (Guru Besar)">
            <i className="fas fa-file-word"></i>
          </a>
        </div>
      </div>

      {/* Activity Progress Section */}
      {activityProgress && (
        <div className="dashboard-section activity-progress-section">
          <h3><i className="fas fa-tasks"></i> Activity Progress: {activityProgress.activityName}</h3>
          <div className="activity-progress-card">
            <div className="progress-header">
              <span className="progress-label">Overall Progress</span>
              <span className="progress-percentage">{activityProgress.overallCompletionRate}%</span>
            </div>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${activityProgress.overallCompletionRate}%` }}
              ></div>
            </div>
            <div className="progress-stats">
              <div className="progress-stat">
                <i className="fas fa-check-circle text-success"></i>
                <span>Completed: {activityProgress.completedSchools} schools</span>
              </div>
              <div className="progress-stat">
                <i className="fas fa-hourglass-half text-warning"></i>
                <span>In Progress: {activityProgress.inProgressSchools} schools</span>
              </div>
              <div className="progress-stat">
                <i className="fas fa-clock text-danger"></i>
                <span>Not Started: {activityProgress.notStartedSchools} schools</span>
              </div>
            </div>
            <div className="progress-details">
              <span>Total Students: {activityProgress.totalStudents.toLocaleString()}</span>
              <span className="separator">•</span>
              <span>With Data: {activityProgress.studentsWithData.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Completion Rate Chart by School - Shows TAHUN-level counts per school */}
      {schools.length > 0 && (
        <CompletionRateChart
          data={schools.map(school => {
            // Use tahun-level counts from the API
            const totalTahun = school.totalTahun || 0;
            const closedTahun = school.closedTahun || 0;
            const openTahun = school.openTahun || 0;
            const notStartedTahun = school.notStartedTahun || 0;

            return {
              code: school.schoolCode,
              completed: closedTahun,        // Number of tahun with CLOSED status (submitted)
              inProgress: openTahun,         // Number of tahun with OPEN status (WIP)
              notStarted: notStartedTahun,   // Number of tahun with no UNFSCHYR record
              total: totalTahun              // Total tahun with students
            };
          })}
          title="School Completion Status (Grades/Tahun)"
          xAxisLabel="School"
        />
      )}

      <div className="dashboard-content">
        {/* Schools Summary Table */}
        <div className="dashboard-section">
          <h3><i className="fas fa-school"></i> Schools Summary ({schools.length} schools)</h3>
          <div className="schools-table-container">
            <table className="schools-table">
              <thead>
                <tr>
                  <th>School Code</th>
                  <th>School Name</th>
                  <th className="text-center">Students</th>
                  <th className="text-center">Completion</th>
                  <th className="text-center">Issues</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {schools.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No schools found in this PPD</td>
                  </tr>
                ) : (
                  schools.map((school) => (
                    <tr key={school.schoolCode}>
                      <td className="school-code">{school.schoolCode}</td>
                      <td className="school-name">{school.schoolName}</td>
                      <td className="text-center">{school.totalStudents}</td>
                      <td className="text-center">
                        <div className="completion-cell">
                          <div className="mini-progress-bar">
                            <div
                              className="mini-progress-fill"
                              style={{ width: `${school.completionRate}%` }}
                            ></div>
                          </div>
                          <span className="completion-text">
                            {school.closedTahun}/{school.totalTahun} Closed ({school.completionRate}%)
                          </span>
                        </div>
                      </td>
                      <td className="text-center">
                        {school.openIssuesCount > 0 ? (
                          <span className="badge badge-warning">{school.openIssuesCount}</span>
                        ) : (
                          <span className="text-muted">0</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span
                          className={`status-badge ${getStatusClass(school.status)} ${school.schoolStatus?.toUpperCase() === 'COMPLETED' ? 'clickable' : ''}`}
                          onClick={() => school.schoolStatus?.toUpperCase() === 'COMPLETED' && handleReopenSchool(school)}
                          title={school.schoolStatus?.toUpperCase() === 'COMPLETED' ? 'Click to reopen this school' : school.status}
                          style={{ cursor: school.schoolStatus?.toUpperCase() === 'COMPLETED' ? 'pointer' : 'default' }}
                        >
                          {school.status}
                          {school.schoolStatus?.toUpperCase() === 'COMPLETED' && (
                            <i className="fas fa-lock" style={{ marginLeft: '5px', fontSize: '0.8em' }}></i>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Issue Reports Summary */}
        {issueReports && (
          <div className="dashboard-section issue-reports-section">
            <h3>
              <i className="fas fa-exclamation-triangle"></i> Issue Reports
              {issueReports.unreadCount > 0 && (
                <span className="badge badge-danger ml-2">{issueReports.unreadCount} unread</span>
              )}
            </h3>

            <div className="issue-stats">
              <div className="issue-stat">
                <span className="issue-stat-label">Open:</span>
                <span className="issue-stat-value badge-danger">{issueReports.openCount}</span>
              </div>
              <div className="issue-stat">
                <span className="issue-stat-label">Pending:</span>
                <span className="issue-stat-value badge-warning">{issueReports.pendingCount}</span>
              </div>
              <div className="issue-stat">
                <span className="issue-stat-label">Resolved (30d):</span>
                <span className="issue-stat-value badge-success">{issueReports.resolvedLast30DaysCount}</span>
              </div>
              {issueReports.highPriorityCount > 0 && (
                <div className="issue-stat">
                  <span className="issue-stat-label">High Priority:</span>
                  <span className="issue-stat-value badge-danger">{issueReports.highPriorityCount}</span>
                </div>
              )}
            </div>

            {issueReports.recentIssues && issueReports.recentIssues.length > 0 && (
              <div className="recent-issues">
                <h4>Recent Issues</h4>
                <div className="issues-list">
                  {issueReports.recentIssues.map((issue) => (
                    <div key={issue.reportId} className={`issue-item ${issue.unreadByAdmin ? 'unread' : ''}`}>
                      <div className="issue-header">
                        <span className="issue-id">#{issue.reportId}</span>
                        <span className={`issue-status ${issue.status.toLowerCase()}`}>{issue.status}</span>
                        {issue.unreadByAdmin && <span className="badge badge-new">NEW</span>}
                      </div>
                      <div className="issue-details">
                        <div className="issue-school">
                          <i className="fas fa-school"></i>
                          <span>{issue.schoolName || issue.schoolCode}</span>
                        </div>
                        <div className="issue-category">
                          <i className="fas fa-tag"></i>
                          <span>{issue.category}</span>
                        </div>
                      </div>
                      <div className="issue-description">{issue.description}</div>
                      <div className="issue-footer">
                        <span className="issue-date">{formatDateTime(issue.createdDate)}</span>
                        {issue.messageCount > 0 && (
                          <span className="issue-messages">
                            <i className="fas fa-comments"></i> {issue.messageCount}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="view-all-issues">
                  <a href="/issue-reports" className="btn btn-outline">
                    View All Issues <i className="fas fa-arrow-right"></i>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* District Audit Log */}
        {recentActivities && recentActivities.length > 0 && (
          <div className="dashboard-section audit-log-section">
            <h3><i className="fas fa-history"></i> District Activity Log</h3>
            <div className="audit-trail-container">
              <table className="audit-trail-table">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>School</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivities.map((activity, idx) => (
                    <tr key={idx}>
                      <td className="audit-datetime">
                        <div className="audit-date">{formatDate(activity.timestamp)}</div>
                        <div className="audit-time-text">{new Date(activity.timestamp).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="audit-school-cell">
                        <div className="audit-school-name">{activity.schoolName || activity.schoolCode || 'N/A'}</div>
                        {activity.schoolCode && activity.schoolName && (
                          <div className="audit-school-code">{activity.schoolCode}</div>
                        )}
                      </td>
                      <td className="audit-action-cell">
                        <span className={`action-badge action-${(activity.actionType || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                          {activity.actionType || 'Unknown'}
                        </span>
                      </td>
                      <td className="audit-user-cell">
                        <div className="audit-user-name">{activity.userName || activity.userEmail || 'System'}</div>
                        {activity.userRole && (
                          <div className="audit-user-role">{activity.userRole}</div>
                        )}
                      </td>
                      <td className="audit-details-cell">
                        {activity.details || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* Cache Info Footer */}
      <div className="dashboard-footer">
        <small className="text-muted">
          Last updated: {formatDateTime(dashboardData.cacheTimestamp)}
        </small>
      </div>
    </div>
  );
};

export default PPDDashboard;
